import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import { ListGroupTask } from '@shared/modules/list/model/list-group-task.model';
import {
  ListTasksV2GroupComponent,
  ListTasksV2GroupEditEvent,
  ListTasksV2TemplateAddEvent,
  ListTasksV2TaskDropEvent,
  ListTasksV2TaskStatusEvent
} from './components/list-tasks-v2-group/list-tasks-v2-group.component';
import { arrMove } from '@shared/helpers/arr-move.helper';

export interface ListTasksV2GroupOrderEvent {
  groups: Array<{ id: number; order: number }>;
}

@Component({
  selector: 'list-tasks-v2',
  standalone: true,
  templateUrl: './list-tasks-v2.component.html',
  styleUrls: ['./list-tasks-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DndModule, ListTasksV2GroupComponent]
})
export class ListTasksV2Component {
  groupsInput = input<ListGroupTask[] | null>(null, { alias: 'groups' });
  loading = input<boolean>(false);
  disableGroupDrag = input<boolean>(false);

  taskDropped = output<ListTasksV2TaskDropEvent>();
  taskStatusChanged = output<ListTasksV2TaskStatusEvent>();
  taskClicked = output<{ task: Task; groupId: number; isProcessGroup: boolean }>();
  groupEditRequested = output<ListTasksV2GroupEditEvent>();
  groupOrderChanged = output<ListTasksV2GroupOrderEvent>();
  addTask = output<ListGroupTask>();
  templateAddSelected = output<ListTasksV2TemplateAddEvent>();

  protected readonly groupsState = signal<ListGroupTask[]>([]);
  protected readonly sortedGroups = computed(() =>
    [...this.groupsState()].sort((a, b) => (a.taskGroup?.sortOrder || 0) - (b.taskGroup?.sortOrder || 0))
  );

  constructor() {
    effect(
      () => {
        const groups = this.groupsInput();
        this.groupsState.set((groups || []).map((group) => this.normalizeGroup(group)));
      },
      { allowSignalWrites: true }
    );
  }

  protected onGroupDrop(event: DndDropEvent): void {
    const payload = event.data as { groupId: number } | null;
    if (!payload) return;

    const current = [...this.sortedGroups()];
    const previousIndex = current.findIndex((group) => group.taskGroup?.id === payload.groupId);
    if (previousIndex < 0) return;

    const dropIndex = Number.isInteger(event.index) ? Number(event.index) : current.length;
    const safeIndex = Math.max(0, Math.min(dropIndex, current.length - 1));

    const moved = arrMove(current, previousIndex, safeIndex).map((group, index) => {
      group.taskGroup.sortOrder = index + 1;
      return group;
    });

    this.groupsState.set(moved);
    this.groupOrderChanged.emit({
      groups: moved.map((group) => ({ id: group.taskGroup.id, order: group.taskGroup.sortOrder }))
    });
  }

  protected onGroupTaskDrop(event: ListTasksV2TaskDropEvent): void {
    const state = structuredClone(this.groupsState());
    const sourceGroupIndex = state.findIndex((group) => group.taskGroup.id === event.sourceGroupId);
    const targetGroupIndex = state.findIndex((group) => group.taskGroup.id === event.targetGroupId);
    if (sourceGroupIndex < 0 || targetGroupIndex < 0) return;

    const sourceCollection = this.getCollection(state[sourceGroupIndex], event.sourceStatus);
    const targetCollection = this.getCollection(state[targetGroupIndex], event.targetStatus);

    const sourceTaskIndex = sourceCollection.findIndex((task) => task.id === event.taskId);
    if (sourceTaskIndex < 0) return;

    const [movedTask] = sourceCollection.splice(sourceTaskIndex, 1);
    const safeIndex = Math.max(0, Math.min(event.index ?? targetCollection.length, targetCollection.length));
    targetCollection.splice(safeIndex, 0, movedTask);

    sourceCollection.forEach((task, index) => (task.sortOrder = index + 1));
    targetCollection.forEach((task, index) => (task.sortOrder = index + 1));

    this.groupsState.set(state);
    this.taskDropped.emit(event);
  }

  protected onAddTask(group: ListGroupTask): void {
    this.addTask.emit(group);
  }

  protected onTaskStatusChanged(event: ListTasksV2TaskStatusEvent): void {
    this.taskStatusChanged.emit(event);
  }

  protected onTaskClicked(event: { task: Task; groupId: number; isProcessGroup: boolean }): void {
    this.taskClicked.emit(event);
  }

  protected onGroupEditRequested(event: ListTasksV2GroupEditEvent): void {
    this.groupEditRequested.emit(event);
  }

  protected onTemplateAddSelected(event: ListTasksV2TemplateAddEvent): void {
    this.templateAddSelected.emit(event);
  }

  private normalizeGroup(group: ListGroupTask): ListGroupTask {
    return {
      ...group,
      tasks: [...(group.tasks || [])],
      completedTasks: [...(group.completedTasks || [])],
      cancelledTasks: [...(group.cancelledTasks || [])]
    };
  }

  private getCollection(group: ListGroupTask, status: 'created' | 'completed' | 'cancelled') {
    if (status === 'completed') return group.completedTasks;
    if (status === 'cancelled') return group.cancelledTasks;
    return group.tasks;
  }
}
